
import { defineComponent, onBeforeMount, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheTable from '@/common/components/UI/TheTable/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import Modal from '@/common/components/UI/Modal/index.vue'
import TableScrollWrapper from '@/common/components/UI/TableScrollWrapper/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'
import Cheap from '@/common/components/UI/Cheap/index.vue'

// module components
import CashOnDeliveriesFilters from '@/modules/CashOnDeliveriesModule/components/CashOnDeliveriesFilters/index.vue'

// classes
import { DataState } from '@/core/classes/DataState.class'

// realisations
import { cash_on_deliveries } from '@/core/realisations/cash_on_deliveries.realisation'

// types
import {
    CashOnDeliveriesExcelResponse,
    CashOnDeliveriesItem,
    CashOnDeliveriesParams,
    UpdateCashOnDeliveriesPayload,
} from '@/core/types/cash_on_deliveries.types'

// composable
import useNotifications from '@/common/composable/useNotifications'
import { useFile } from '@/common/composable/useFile'
import useAccount from '@/common/composable/useAccount'

// constants
import { LtlStatusColors } from '@/core/constants/columns/LTL.columns'
export const CashOnDeliveriesColumns = [
    {
        name: 'Клиент',
        key: 'companyName',
    },
    {
        name: 'Номер заказа',
        key: 'orderNumber',
    },
    {
        name: 'Номер накладной',
        key: 'invoiceNumber',
    },
    {
        name: 'Статус',
        key: 'statusName',
    },
    {
        name: 'Направление',
        key: 'direction',
    },
    {
        name: 'НП планируемый по принятым заказам',
        key: 'codPayment',
    },
    {
        name: 'НП фактический принятый курьером',
        key: 'actualPayment',
    },
    {
        name: 'НП перечисленный заказчику',
        key: 'clientAmount',
    },
    {
        name: 'Дата перечисления заказчику',
        key: 'clientDate',
    },
]

export default defineComponent({
    components: {
        PageTitle,
        FailedFetch,
        CashOnDeliveriesFilters,
        TheButton,
        TheTable,
        TableScrollWrapper,
        Modal,
        TheInput,
        Checkbox,
        Cheap,
    },
    setup() {
        const notifications = useNotifications()
        const { saveFileByURL } = useFile()

        const state = reactive({
            cash_on_deliveries: new DataState<CashOnDeliveriesItem>({}),
            limit: 50,
            //
            is_loading: false,
            is_update: false,
            invoiceId: null as number | null,
            actualPayment: null as string | null,
            clientAmount: null as string | null,
            clientDate: null as string | null,
            isCash: 0,
        })

        const filters_state = reactive({
            companyId: null as number | null,
            statusIds: [] as number[],
            startTime: null as string | null,
            endTime: null as string | null,
            invoiceNumber: null as string | null,
        })

        const setFilters = (params: CashOnDeliveriesParams) => {
            filters_state.companyId = params.companyId
            filters_state.statusIds = params.statusIds
            filters_state.startTime = params.startTime
            filters_state.endTime = params.endTime
            filters_state.invoiceNumber = params.invoiceNumber
        }

        const unsetFilters = () => {
            filters_state.companyId = null
            filters_state.statusIds = []
            filters_state.startTime = null
            filters_state.endTime = null
            filters_state.invoiceNumber = null
        }

        const loadCashOnDeliveries = async (page: number) => {
            state.cash_on_deliveries.pagination.current_page = page

            try {
                state.cash_on_deliveries.is_loading = true

                const params: CashOnDeliveriesParams = {
                    page: state.cash_on_deliveries.pagination.current_page,
                    limit: state.limit,
                    ...filters_state,
                }

                const response = await cash_on_deliveries.getList(params)

                state.cash_on_deliveries.list = response.data
                state.cash_on_deliveries.pagination.total_pages = response.meta.last_page
                state.cash_on_deliveries.pagination.total_items = response.meta.total

                state.cash_on_deliveries.is_failed = false
                state.cash_on_deliveries.failure.error_state.is_cancel = false
            } catch (error) {
                state.cash_on_deliveries.failure.catchError(error)

                if (!state.cash_on_deliveries.failure.error_state.is_cancel) {
                    state.cash_on_deliveries.is_failed = true
                }
            } finally {
                if (!state.cash_on_deliveries.failure.error_state.is_cancel) {
                    state.cash_on_deliveries.is_loading = false
                }
            }
        }

        const applyFilters = (params: CashOnDeliveriesParams) => {
            setFilters(params)
            loadCashOnDeliveries(1)
        }

        const resetFilters = () => {
            unsetFilters()
            loadCashOnDeliveries(1)
        }

        const getExcelPayload = () => {
            return {
                statusIds: filters_state.statusIds,
                companyId: filters_state.companyId,
                startTime: filters_state.startTime,
                endTime: filters_state.endTime,
                invoiceNumber: filters_state.invoiceNumber,
            }
        }

        const saveExcel = (response: CashOnDeliveriesExcelResponse | null) => {
            if (!response) return

            const download_url = window.URL.createObjectURL(response)
            let file_name = 'Отчеты по НП'

            if (filters_state.startTime) file_name += ` от ${filters_state.startTime}`
            if (filters_state.endTime) file_name += ` до ${filters_state.endTime}`

            saveFileByURL(download_url, file_name)
        }

        const exportExcel = async () => {
            try {
                state.cash_on_deliveries.is_loading = true

                const response = await cash_on_deliveries.exportExcel(getExcelPayload())
                saveExcel(response)
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'Ошибка скачивания Excel',
                })
            } finally {
                state.cash_on_deliveries.is_loading = false
            }
        }

        const onLimitChange = (limit: number) => {
            state.limit = limit
            loadCashOnDeliveries(state.cash_on_deliveries.pagination.current_page)
        }

        const toDateTimeLocal = (dateStr: string) => {
            const dateArr = dateStr.split(':')
            return `${dateArr[0]}:${dateArr[1]}`
        }

        const toggleUpdate = (item?: CashOnDeliveriesItem) => {
            if (item) {
                state.invoiceId = item.id
                state.actualPayment = item.actualPayment
                state.clientAmount = item.clientAmount
                state.clientDate = item.clientDate ? toDateTimeLocal(item.clientDate) : null
            } else {
                state.invoiceId = null
                state.actualPayment = null
                state.clientAmount = null
                state.clientDate = null
                state.isCash = 0
            }

            state.is_update = !state.is_update
        }

        const updateCashOnDeliveries = async () => {
            if (!state.invoiceId || !state.actualPayment) {
                notifications.notify({
                    title: 'Заполните необходимые поля',
                    type: 'danger',
                })
                return
            }

            try {
                state.is_loading = true

                const dateStr = state.clientDate
                    ? state.clientDate.split('T').join(' ') + ':00'
                    : ''

                const payload: UpdateCashOnDeliveriesPayload = {
                    invoiceId: state.invoiceId,
                    actualPayment: state.actualPayment,
                    isCash: state.isCash,
                }

                if (dateStr) {
                    payload.clientDate = dateStr
                }
                if (state.clientAmount) {
                    payload.clientAmount = state.clientAmount
                }
                await cash_on_deliveries.updateCashOnDeliveries(payload)
                notifications.notify({ title: 'Данные успешно отредактированы', type: 'success' })
                toggleUpdate()
                loadCashOnDeliveries(1)
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'Произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        onBeforeMount(() => {
            loadCashOnDeliveries(1)
        })

        const { havePermissions } = useAccount()

        const getCheapClass = (status_id: number) => {
            if (LtlStatusColors[status_id]) return LtlStatusColors[status_id]
            else if (status_id === 51) return 'red'
            else if (![1, 4].includes(status_id)) return 'yellow'

            return ''
        }

        const cutName = (name: string, max_length: number): string => {
            if (!name) return '-'

            if (name && name.length > max_length) {
                let name_parts = name.split('')
                name_parts = name_parts.splice(0, max_length - 1)
                name_parts.push('...')
                return name_parts.join('')
            }

            return name
        }

        return {
            state,
            getCheapClass,
            cutName,
            loadCashOnDeliveries,
            applyFilters,
            resetFilters,
            exportExcel,
            onLimitChange,
            CashOnDeliveriesColumns,
            toggleUpdate,
            updateCashOnDeliveries,
            havePermissions,
        }
    },
})
